import differenceInDays from 'date-fns/differenceInDays';
import { startOfDay, endOfDay } from 'date-fns';

const padTo2Digits = (number: number) => number.toString().padStart(2, '0');

const formatDate = (date: Date) => [
  padTo2Digits(date.getMonth() + 1),
  padTo2Digits(date.getDate()),
  date.getFullYear(),
].join('/');

const maxDateFrom2Dates = (dates: Date[]) => new Date(Math.max(...dates.map(Number)));
const minDateFrom2Dates = (dates: Date[]) => new Date(Math.min(...dates.map(Number)));

const getPriorDateFromSpecificDate = (days: number, startDate: Date | string) => {
  startDate = new Date(startDate);
  const daysAgo = new Date(startDate.getTime());
  daysAgo.setDate(startDate.getDate() - days);
  return daysAgo;
};

const getFutureDateFromSpecificDate = (days: number, startDate: Date | string) => {
  startDate = new Date(startDate);
  const futureDays = new Date(startDate.getTime());
  futureDays.setDate(startDate.getDate() + days);
  return futureDays;
};

const formatDateToISOString = (date: Date | string | undefined) => {
  if (typeof date === 'string') {
    return new Date(date).toISOString();
  } if (date instanceof Date) {
    return date.toISOString();
  }
  return undefined;
};

const elapsedTimeInSeconds = (startTime: any, currTime: any) => {
  const elapsedMilliSec = currTime - startTime;
  const elapsedSec = elapsedMilliSec / 1000;
  return elapsedSec;
};

export const cleanOffsetFromDate = (date: Date) => new Date(Number(date) - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];

export const getStartOfDay = (date: Date) => startOfDay(date);
export const getEndOfDay = (date: Date) => endOfDay(date);

export const getMonthShortName = (monthNum: number) => {
  if (monthNum === 1) return 'Jan';
  if (monthNum === 2) return 'Feb';
  if (monthNum === 3) return 'Mar';
  if (monthNum === 4) return 'Apr';
  if (monthNum === 5) return 'May';
  if (monthNum === 6) return 'Jun';
  if (monthNum === 7) return 'Jul';
  if (monthNum === 8) return 'Aug';
  if (monthNum === 9) return 'Sep';
  if (monthNum === 10) return 'Oct';
  if (monthNum === 11) return 'Nov';
  if (monthNum === 12) return 'Dec';
  return '';
};

const formatDateV2 = (dateStr: string | undefined, includeYear: boolean, leadingMonth?: boolean) => {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  const dateOnly = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
  const year = dateOnly.getFullYear();
  const day = dateOnly.getDate();
  const monthNum = dateOnly.getMonth() + 1;
  const dayToDisplay = day.toString().length < 2 ? `0${day}` : day;
  const monthToDisplay = getMonthShortName(monthNum);
  const yearToDisplay = includeYear ? year : '';
  if (leadingMonth) return `${monthToDisplay} ${dayToDisplay} ${includeYear ? `, ${yearToDisplay}` : ''}`;
  return `${dayToDisplay} ${monthToDisplay} ${includeYear ? year : ''}`;
};

export const getNumOfOverdueDays = (dueDate: string) => differenceInDays(startOfDay(new Date()), startOfDay(new Date(dueDate)));
export const getNumOfDaysLeft = (targetDate: string) => differenceInDays(startOfDay(new Date(targetDate)), startOfDay(new Date()));

export const isSameDate = (date1: Date, date2: Date) => date1.getFullYear() === date2.getFullYear()
    && date1.getMonth() === date2.getMonth()
    && date1.getDate() === date2.getDate();

const convertLocalToUTCStart = (localTime: Date) => {
  // Create a Date object with the local time
  const localDate = new Date(localTime);

  // Get the UTC time by subtracting the local time zone offset
  const utcTime = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000);

  // Set the time to the start of the day (midnight)
  utcTime.setUTCHours(0, 0, 0, 0);

  return utcTime;
};

export {
  formatDate,
  formatDateToISOString,
  getPriorDateFromSpecificDate,
  getFutureDateFromSpecificDate,
  maxDateFrom2Dates,
  minDateFrom2Dates,
  elapsedTimeInSeconds,
  formatDateV2,
  convertLocalToUTCStart,
};
